import { createAsyncThunk } from '@reduxjs/toolkit';
import type { Engin } from '@/types';
import api from '@/api/client';
import { parsePaginatedResponse } from '@/api/types';
import { setEngins, editEngin, removeEngin } from '../slices/enginSlice';
import { setToast } from '../slices/toastSlice';

const PAGE_SIZE = 10;

export const fetchEngins = createAsyncThunk<void, number | undefined>(
  'engin/fetch',
  async (pageNo, { dispatch, rejectWithValue }) => {
    try {
      if (pageNo != null) {
        const { data } = await api.get('/engins/', { params: { pageNo, pageSize: PAGE_SIZE, sortBy: 'id' } });
        const { list, pageNum, totalPages } = parsePaginatedResponse<Engin>(data);
        dispatch(setEngins({ data: list, pageNum, totalPages }));
      } else {
        const { data } = await api.get<{ data: Engin[] }>('/engins/');
        dispatch(setEngins(data.data ?? []));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur chargement engins';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const createEngin = createAsyncThunk<boolean, Partial<Engin>, { rejectValue: string }>(
  'engin/create',
  async (engin, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/engins/', engin);
      await dispatch(fetchEngins(0));
      dispatch(setToast({ message: 'Engin créé', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur création';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const updateEngin = createAsyncThunk<boolean, Engin, { rejectValue: string }>(
  'engin/update',
  async (engin, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/engins/${engin.id}`, engin);
      dispatch(editEngin(engin));
      dispatch(setToast({ message: 'Engin modifié', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur modification';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const deleteEngin = createAsyncThunk<boolean, Engin, { rejectValue: string }>(
  'engin/delete',
  async (engin, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/engins/${engin.id}`);
      dispatch(removeEngin(engin));
      dispatch(setToast({ message: 'Engin supprimé', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur suppression';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);
